import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../api";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";

export default function MultiArchive() {
  const [multiEvents, setMultiEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // The backend side of this feature (POST /archive/multiday) always
  // existed, but no frontend ever actually called it -- the empty-state
  // message pointed at "the Results page" for creation, which has no such
  // flow either. Built properly here instead of leaving it a dead end.
  const [showCreate, setShowCreate] = useState(false);
  const [archivedEvents, setArchivedEvents] = useState([]);
  const [newName, setNewName] = useState("");
  const [checkedIds, setCheckedIds] = useState([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  useEffect(() => { loadMultiEvents(); }, []);

  const loadMultiEvents = async () => {
    try {
      const res = await api.get("/archive/multiday");
      setMultiEvents(res.data);
    } catch(e) { setError("Failed to load"); }
    finally { setLoading(false); }
  };

  const openCreate = async () => {
    setShowCreate(true);
    setNewName("");
    setCheckedIds([]);
    setCreateError("");
    try {
      const res = await api.get("/archive");
      setArchivedEvents(res.data);
    } catch(e) { setCreateError("Couldn't load archived competitions"); }
  };

  const toggleChecked = (id) => {
    setCheckedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const submitCreate = async () => {
    if (!newName.trim()) { setCreateError("Enter a name"); return; }
    if (checkedIds.length === 0) { setCreateError("Pick at least one competition"); return; }
    setCreating(true);
    setCreateError("");
    try {
      const res = await api.post("/archive/multiday", { name: newName.trim(), archived_event_ids: checkedIds });
      setShowCreate(false);
      await loadMultiEvents();
      loadDetail(res.data.id);
    } catch(e) { setCreateError(e.response?.data?.error || "Couldn't create group"); }
    finally { setCreating(false); }
  };

  const loadDetail = async (id) => {
    try {
      setDetail(null);
      setSelected(id);
      const res = await api.get("/archive/multiday/" + id);
      setDetail(res.data);
    } catch(e) { setError("Failed to load details"); }
  };

  // Was window.confirm -- same native-dialog freeze risk found and fixed
  // in Player Lists earlier today (can leave the page looking frozen/
  // unresponsive after use). Same fix: an in-page modal instead.
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const deleteEvent = (id) => setConfirmDeleteId(id);
  const confirmDelete = async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      await api.delete("/archive/multiday/" + id);
      setMultiEvents(prev => prev.filter(e => e.id !== id));
      if (selected === id) { setSelected(null); setDetail(null); }
    } catch(e) { setError("Failed to delete"); }
  };

  const MATCH_PLAY_FORMATS = ['Singles Match Play','Better Ball Match Play','Fourball Match Play','Foursomes Match Play','Greensomes Match Play'];
  const dayIsMedal = (d) => d.game_type?.includes("Medal") || d.game_type?.includes("Stroke");
  const dayIsMatchPlay = (d) => MATCH_PLAY_FORMATS.includes(d.game_type);
  const matchResultText = (m, d) => {
    const isInterClub = !!d.team_a_name;
    const winnerLabel = m.winning_side === 'A' ? (isInterClub ? d.team_a_name : m.team_a_players)
      : m.winning_side === 'B' ? (isInterClub ? d.team_b_name : m.team_b_players)
      : null;
    return winnerLabel ? `${winnerLabel} won ${m.margin || ''}`.trim() : 'Halved';
  };

  // Rewritten 2026-10-15: each day's section is now the exact original
  // results (same rows, same stored rank) rather than a recomputed
  // combined cross-tab -- see the backend route's own comment for why.
  const exportToExcel = () => {
    if (!detail) return;
    const aoa = [];
    aoa.push([detail.event.name]);
    if (detail.event.description) aoa.push([detail.event.description]);
    aoa.push([]);

    detail.days.forEach(d => {
      aoa.push([`Day ${d.day_number}`]);
      aoa.push([
        d.date ? new Date(d.date).toLocaleDateString('en-GB') : '',
        "Venue:", d.venue || '', "Course:", d.course_name || '', "Format:", d.game_type || ''
      ]);
      aoa.push([]);

      if (dayIsMatchPlay(d)) {
        aoa.push(["Match", "Team A", "Team B", "Result"]);
        (d.matches || []).forEach(m => aoa.push([m.grp, m.team_a_players, m.team_b_players, matchResultText(m, d)]));
      } else {
        aoa.push(dayIsMedal(d) ? ["Pos", "Player", "HCP", "Gross", "Net"] : ["Pos", "Player", "HCP", "Gross", "Net", "Pts"]);
        (d.results || []).forEach(r => aoa.push(
          dayIsMedal(d) ? [r.rank, r.player_name, r.handicap, r.gross, r.net] : [r.rank, r.player_name, r.handicap, r.gross, r.net, r.points]
        ));
      }
      aoa.push([]);

      if (d.nearest_pin || d.nearest_pin2 || d.longest_drive || (d.twos || []).length) {
        if (d.nearest_pin) aoa.push(["Nearest Pin:", d.nearest_pin]);
        if (d.nearest_pin2) aoa.push(["Nearest Pin 2:", d.nearest_pin2]);
        if (d.longest_drive) aoa.push(["Longest Drive:", d.longest_drive]);
        if ((d.twos || []).length) aoa.push(["Twos:", d.twos.map(t => `${t.player_name} (Hole ${t.hole})`).join(", ")]);
      }
      aoa.push([]);
      aoa.push([]);
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    XLSX.utils.book_append_sheet(wb, ws, "Multi Day Results");
    XLSX.writeFile(wb, detail.event.name.replace(/\s+/g, '_') + ".xlsx");
  };

  const exportToPDF = () => {
    if (!detail) return;
    const doc = new jsPDF();
    let y = 20;
    const ensureRoom = (needed) => { if (y + needed > 280) { doc.addPage(); y = 20; } };

    doc.setFontSize(16);
    doc.text(detail.event.name, 14, y);
    y += 8;
    if (detail.event.description) { doc.setFontSize(10); doc.text(detail.event.description, 14, y); y += 8; }

    detail.days.forEach(d => {
      ensureRoom(30);
      doc.setFontSize(13); doc.setFont(undefined, 'bold');
      doc.text(`Day ${d.day_number}`, 14, y);
      doc.setFont(undefined, 'normal'); y += 6;
      doc.setFontSize(10);
      doc.text(`${d.date ? new Date(d.date).toLocaleDateString('en-GB') : ''}   Venue: ${d.venue || '-'}   Course: ${d.course_name || '-'}   Format: ${d.game_type || '-'}`, 14, y);
      y += 8;

      if (dayIsMatchPlay(d)) {
        const grpX = 14, aX = 32, bX = 90, resX = 150;
        doc.setFont(undefined, 'bold');
        doc.text("Match", grpX, y); doc.text("Team A", aX, y); doc.text("Team B", bX, y); doc.text("Result", resX, y);
        doc.setFont(undefined, 'normal'); y += 5; doc.line(14, y, 196, y); y += 6;
        (d.matches || []).forEach(m => {
          ensureRoom(7);
          doc.text(String(m.grp ?? '-'), grpX, y);
          doc.text(m.team_a_players || '', aX, y);
          doc.text(m.team_b_players || '', bX, y);
          doc.text(matchResultText(m, d), resX, y);
          y += 6;
        });
      } else {
        const posX = 14, playerX = 32, hcpX = 110, grossX = 140, netX = 163, ptsX = 185;
        doc.setFont(undefined, 'bold');
        doc.text("Pos", posX, y); doc.text("Player", playerX, y); doc.text("HCP", hcpX, y);
        doc.text("Gross", grossX, y); doc.text("Net", netX, y);
        if (!dayIsMedal(d)) doc.text("Pts", ptsX, y);
        doc.setFont(undefined, 'normal'); y += 5; doc.line(14, y, 196, y); y += 6;
        (d.results || []).forEach(r => {
          ensureRoom(7);
          doc.text(String(r.rank ?? '-'), posX, y);
          doc.text(r.player_name || '', playerX, y);
          doc.text(String(r.handicap ?? '-'), hcpX, y);
          doc.text(String(r.gross ?? '-'), grossX, y);
          doc.text(String(r.net ?? '-'), netX, y);
          if (!dayIsMedal(d)) doc.text(String(r.points ?? '-'), ptsX, y);
          y += 6;
        });
      }
      y += 4;

      if (d.nearest_pin || d.nearest_pin2 || d.longest_drive || (d.twos || []).length) {
        ensureRoom(24);
        if (d.nearest_pin) { doc.text(`Nearest Pin: ${d.nearest_pin}`, 14, y); y += 6; }
        if (d.nearest_pin2) { doc.text(`Nearest Pin 2: ${d.nearest_pin2}`, 14, y); y += 6; }
        if (d.longest_drive) { doc.text(`Longest Drive: ${d.longest_drive}`, 14, y); y += 6; }
        if ((d.twos || []).length) { doc.text(`Twos: ${d.twos.map(t => t.player_name + " (Hole " + t.hole + ")").join(", ")}`, 14, y); y += 6; }
      }
      y += 8;
    });

    doc.save(detail.event.name.replace(/\s+/g, '_') + ".pdf");
  };

  return (
    <Layout>
      <div className="flex h-full">
        {/* Left panel - list */}
        <div style={{ width: "280px", borderRight: "1px solid #e5e7eb", overflowY: "auto", flexShrink: 0 }}>
          <div className="bg-green-800 text-white px-4 py-2 font-semibold text-sm flex items-center justify-between">
            Multi-Day Competitions
            <button onClick={openCreate} className="bg-white text-green-800 text-xs font-bold px-2 py-1 rounded hover:bg-green-100">+ New</button>
          </div>
          {loading && <div className="px-4 py-3 text-sm text-gray-500">Loading...</div>}
          {error && <div className="px-4 py-2 text-sm text-red-600">{error}</div>}
          {!loading && multiEvents.length === 0 && (
            <div className="px-4 py-4 text-sm text-gray-400">No multi-day competitions yet. Click "+ New" above to group some archived competitions together — e.g. a whole season, or a multi-day trip.</div>
          )}
          {multiEvents.map(ev => (
            <div key={ev.id}
              onClick={() => loadDetail(ev.id)}
              className={"px-4 py-3 cursor-pointer border-b border-gray-100 hover:bg-green-50 " + (selected === ev.id ? "bg-green-100" : "")}>
              <div className="font-semibold text-sm text-green-800">{ev.name}</div>
              <div className="text-xs text-gray-500 mt-0.5">{ev.day_count} days · {new Date(ev.created_at).toLocaleDateString('en-GB')}</div>
              {ev.description && <div className="text-xs text-gray-400 mt-0.5 italic">{ev.description}</div>}
              <button onClick={e => { e.stopPropagation(); deleteEvent(ev.id); }}
                className="text-xs text-red-400 hover:text-red-600 mt-1">Delete</button>
            </div>
          ))}
        </div>

        {/* Right panel - results */}
        <div className="flex-1 overflow-auto">
          {!detail && (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">
              Select a competition to view results
            </div>
          )}
          {detail && (
            <div>
              <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-green-800">{detail.event.name}</h2>
                  {detail.event.description && <p className="text-sm text-gray-500">{detail.event.description}</p>}
                </div>
                <div className="flex gap-2">
                  <button onClick={exportToExcel} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded">📊 Export Excel</button>
                  <button onClick={exportToPDF} className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1.5 rounded">📄 Export PDF</button>
                </div>
              </div>

              {/* Each day shown exactly as it was originally archived -- same
                  rows, same stored rank/gross/net/points, no recomputed
                  summary. Per Mike directly: "i need to see the archives as
                  they were, the clue is in the name archive." */}
              {detail.days.map(d => (
                <div key={d.day_number} className="mb-6 border-b-4 border-green-100">
                  <div className="bg-green-50 px-6 py-2 border-t border-green-200">
                    <div className="font-bold text-green-800 text-sm">
                      Day {d.day_number}: {d.date ? new Date(d.date).toLocaleDateString('en-GB') : ''} — {d.venue || d.course_name} — {d.game_type}
                    </div>
                    <div className="flex gap-4 flex-wrap mt-1 text-xs">
                      {d.nearest_pin && <span className="text-green-700 font-semibold">📍 Nearest Pin: {d.nearest_pin}</span>}
                      {d.nearest_pin2 && <span className="text-green-700 font-semibold">📍 Nearest Pin 2: {d.nearest_pin2}</span>}
                      {d.longest_drive && <span className="text-blue-700 font-semibold">🏌️ Longest Drive: {d.longest_drive}</span>}
                      {(d.twos || []).length > 0 && <span className="text-purple-700 font-semibold">🎯 Twos: {d.twos.map(t => t.player_name + " (Hole " + t.hole + ")").join(", ")}</span>}
                      <a href={"/archive?event=" + d.archived_event_id} target="_blank" rel="noopener noreferrer" className="text-gray-500 underline">Open full original ↗</a>
                    </div>
                  </div>

                  {dayIsMatchPlay(d) ? (
                    <table className="w-full text-sm border-collapse">
                      <thead className="bg-green-800 text-white">
                        <tr>
                          <th className="px-4 py-2 text-center w-16">Match</th>
                          <th className="px-4 py-2 text-left">Team A</th>
                          <th className="px-4 py-2 text-left">Team B</th>
                          <th className="px-4 py-2 text-left">Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(d.matches || []).map((m, i) => (
                          <tr key={m.id} className={"border-b border-gray-200 " + (i % 2 === 0 ? "bg-white" : "bg-green-50")}>
                            <td className="px-4 py-2 text-center font-bold">{m.grp}</td>
                            <td className="px-4 py-2">{m.team_a_players}</td>
                            <td className="px-4 py-2">{m.team_b_players}</td>
                            <td className="px-4 py-2 font-bold text-blue-700">{matchResultText(m, d)}</td>
                          </tr>
                        ))}
                        {(d.matches || []).length === 0 && (
                          <tr><td colSpan={4} className="text-center py-6 text-gray-500">No match results found.</td></tr>
                        )}
                      </tbody>
                    </table>
                  ) : (
                    <table className="w-full text-sm border-collapse">
                      <thead className="bg-green-800 text-white">
                        <tr>
                          <th className="px-4 py-2 text-center w-12">Pos</th>
                          <th className="px-4 py-2 text-left">Player</th>
                          <th className="px-4 py-2 text-center w-16">HCP</th>
                          <th className="px-4 py-2 text-center w-16">Gross</th>
                          <th className="px-4 py-2 text-center w-16">Net</th>
                          {!dayIsMedal(d) && <th className="px-4 py-2 text-center w-16">Pts</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {(d.results || []).map((r, i) => (
                          <tr key={r.id} className={"border-b border-gray-200 " + (i % 2 === 0 ? "bg-white" : "bg-green-50")}>
                            <td className="px-4 py-2 text-center font-bold">{r.rank}</td>
                            <td className="px-4 py-2">{r.player_name}</td>
                            <td className="px-4 py-2 text-center">{r.handicap}</td>
                            <td className="px-4 py-2 text-center">{r.gross}</td>
                            <td className="px-4 py-2 text-center">{r.net}</td>
                            {!dayIsMedal(d) && <td className="px-4 py-2 text-center font-bold text-blue-700">{r.points}</td>}
                          </tr>
                        ))}
                        {(d.results || []).length === 0 && (
                          <tr><td colSpan={6} className="text-center py-6 text-gray-500">No results found.</td></tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showCreate && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
          <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"480px",width:"100%",maxHeight:"80vh",display:"flex",flexDirection:"column",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:"#166534",marginBottom:"12px"}}>📅 New Multi-Day Competition</div>
            <input type="text" placeholder="Name (e.g. 2026 Season, Celtic Manor Trip)" value={newName}
              onChange={e => setNewName(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 text-sm mb-3" autoFocus />
            <div style={{fontSize:"12px",fontWeight:600,color:"#374151",marginBottom:"6px"}}>
              Pick which archived competitions belong in this group ({checkedIds.length} selected):
            </div>
            <div style={{flex:1,overflowY:"auto",border:"1px solid #e5e7eb",borderRadius:"8px",marginBottom:"12px"}}>
              {archivedEvents.length === 0 && <div className="text-sm text-gray-400 p-3">No archived competitions yet.</div>}
              {archivedEvents.map(ev => (
                <label key={ev.id} className="flex items-center gap-2 px-3 py-2 text-sm border-b border-gray-100 hover:bg-green-50 cursor-pointer">
                  <input type="checkbox" checked={checkedIds.includes(ev.id)} onChange={() => toggleChecked(ev.id)} />
                  <span>{ev.name || ev.venue} — {ev.date ? new Date(ev.date).toLocaleDateString('en-GB') : ''}</span>
                </label>
              ))}
            </div>
            {createError && <div className="text-sm text-red-600 mb-2">{createError}</div>}
            <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
              <button onClick={() => setShowCreate(false)}
                style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                Cancel
              </button>
              <button onClick={submitCreate} disabled={creating}
                style={{background:"#166534",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:700,cursor:"pointer",opacity:creating?0.6:1}}>
                {creating ? "Creating…" : "Create Group"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteId !== null && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
          <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"420px",width:"100%",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:"#b91c1c",marginBottom:"8px"}}>⚠️ Delete this group?</div>
            <div style={{fontSize:"13px",color:"#374151",marginBottom:"16px"}}>
              This only removes the saved grouping. The original archived competitions inside it are untouched and stay exactly as they are.
            </div>
            <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
              <button onClick={() => setConfirmDeleteId(null)}
                style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                Cancel
              </button>
              <button onClick={confirmDelete}
                style={{background:"#b91c1c",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:700,cursor:"pointer"}}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
